import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from "@mui/material/InputAdornment";
import Stack from '@mui/material/Stack';

import Form from '../Common/Form';
import ItemPicker from '../Common/Form/ItemPicker';
import LoadingDetailsView from '../Common/Skeletons/LoadingDetailsView';

import ItemCategoriesList from './ItemCategoriesList';

const ProductForm = ({
    editMode = false,
}) => {

    const { tenantApi } = useApis();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [initialData, setInitialData] = useState({});
    const [formData, setFormData] = useState({ type: 'PRODUCT' }); // Silently set type to PRODUCT
    const [loading, setLoading] = useState(false);

    // Setting initial data for the ItemPicker components
    const [itemCategory, setItemCategory] = useState({ name: '', id: null });

    // Load item data if in edit mode
    const { id } = useParams();
    const loadformData = async () => {
        setLoading(true);
        try {
            const res = await tenantApi(`/tenants/items/${id}`);
            const item = res.data;
            setInitialData(item);

            setFormData({
                name: item?.name,
                brand: item?.brand,
                model: item?.model,
                partNumber: item?.partNumber,
                code: item?.code,
                type: 'PRODUCT', // Always PRODUCT
                uom: item?.uom,
                unitPrice: item?.unitPrice,
                description: item?.description,
                reorderLevel: item?.reorderLevel,
            });
            setItemCategory({ name: item?.category?.name || "", id: item?.category?.id || null });
            console.debug("Product data loaded successfully:", item);

        } catch (error) {
            console.error("Error loading product data", error);
            showNotification(error.message || "Failed to load product details", 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (editMode) {
            loadformData();
        }
    }, [editMode]);

    // --- Handle input changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Debug formData update
    useEffect(() => {
        console.debug('Product Form Data updated: ', formData);
    }, [formData]);

    // Show skeleton while loading initial data (if in edit mode)
    if (loading)
        return <LoadingDetailsView />

    return (
        <Box sx={{ padding: { xs: 2, sm: 3 }, maxWidth: '800px' }}>
            <Form
                resourceName='Product'
                apiPath='/tenants/items'
                formData={formData}
                requiredFields={[
                    { name: 'name', label: 'Name' },
                    { name: 'uom', label: 'Unit of Measure' },
                    { name: 'unitPrice', label: 'Unit Price' },
                ]}
                editMode={editMode}
                onSubmit={() => {
                    navigate(-1);
                }}
                onCancel={() => {
                    navigate(-1);
                }}
            >
                <Stack spacing={3}>
                    {/* Item Category */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <ItemPicker
                            label="Product category"
                            name="itemCategoryId"
                            initialValue={itemCategory}
                            ListView={ItemCategoriesList}
                            listViewProps={{ type: 'PRODUCT' }}
                            addButton={true}
                            onChange={handleChange}
                        />
                    </Stack>

                    {/* Name */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Name"
                            name="name"
                            value={formData.name || ''}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                    </Stack>

                    {/* Code */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Item code"
                            name="code"
                            value={formData.code || ''}
                            onChange={handleChange}
                            fullWidth
                            sx={{ maxWidth: '370px' }}
                            disabled={editMode && !!initialData.code}
                        />
                    </Stack>

                    {/* UOM + Unit Price */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Unit of Measure (UOM)"
                            name="uom"
                            placeholder='kg, l, m, pcs, ...'
                            value={formData.uom || ''}
                            onChange={handleChange}
                            fullWidth
                            disabled={editMode}
                            inputProps={{ maxLength: 5 }}
                            required
                        />

                        <TextField
                            label="Unit price (selling price)"
                            name="unitPrice"
                            type='number'
                            placeholder='0.00'
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <span style={{ color: "#666", userSelect: "none" }}>
                                            {formData.uom ? `per ${formData.uom}` : ''}
                                        </span>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                min: 0,
                                step: "0.01",
                                onWheel: (e) => e.currentTarget.blur(),
                            }}
                            value={formData.unitPrice || ''}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                    </Stack>

                    {/* Reorder Level */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Reorder level"
                            name="reorderLevel"
                            type='number'
                            placeholder='0.00'
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <span style={{ color: "#666", userSelect: "none" }}>
                                            {formData.uom ? `${formData.uom}` : ''}
                                        </span>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                min: 0,
                                step: "0.01",
                                onWheel: (e) => e.currentTarget.blur(),
                            }}
                            value={formData.reorderLevel || ''}
                            onChange={handleChange}
                            fullWidth
                            sx={{ maxWidth: '370px' }}
                        />
                    </Stack>

                    {/* Description */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description || ''}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            fullWidth
                        />
                    </Stack>
                </Stack>
            </Form>
        </Box>
    );
};

export default ProductForm;
